import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, User, Building2, Briefcase, LogOut, Edit2, Trash2, Check, X, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(location.state?.justLoggedIn ? 'Successfully signed in!' : '');
  
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    content: '',
    company_name: '',
    industry: ''
  });

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Skip if returning from OAuth callback (AuthCallback will handle it)
      if (window.location.hash?.includes('session_id=')) {
        return;
      }

      // Skip if user already set from navigation state
      if (user) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [user]);

  // Fetch reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reviews`);
        if (response.ok) {
          const data = await response.json();
          setReviews(data.reviews || []);
        }
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
      }
    };

    fetchReviews();
  }, []);

  // Fetch user's review if logged in
  useEffect(() => {
    const fetchMyReview = async () => {
      if (!user) return;
      
      try {
        const response = await fetch(`${API_URL}/api/reviews/my`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.review) {
            setMyReview(data.review);
            setFormData({
              rating: data.review.rating,
              title: data.review.title,
              content: data.review.content,
              company_name: data.review.company_name || '',
              industry: data.review.industry || ''
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch my review:', error);
      }
    };

    fetchMyReview();
  }, [user]);

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/reviews';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
      setMyReview(null);
      setMessage('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    try {
      const url = myReview 
        ? `${API_URL}/api/reviews/my`
        : `${API_URL}/api/reviews`;
      
      const response = await fetch(url, {
        method: myReview ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setShowForm(false);
        if (!myReview) {
          setMyReview({ ...formData, status: 'pending' });
        }
      } else {
        setMessage(data.detail || 'Failed to submit review');
      }
    } catch (error) {
      setMessage('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!window.confirm('Are you sure you want to delete your review?')) return;

    try {
      const response = await fetch(`${API_URL}/api/reviews/my`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setMyReview(null);
        setFormData({ rating: 5, title: '', content: '', company_name: '', industry: '' });
        setMessage('Review deleted successfully');
      }
    } catch (error) {
      setMessage('Failed to delete review');
    }
  };

  const StarRating = ({ rating, onRate, interactive = false }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? 'button' : undefined}
          onClick={() => interactive && onRate(star)}
          className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
          disabled={!interactive}
        >
          <Star 
            className={`w-6 h-6 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-6">
        <div className="max-w-6xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">
                Client <span className="text-cyan-400">Reviews</span>
              </h1>
              <p className="text-slate-300 mt-2">See what our clients say about working with us</p>
            </div>
            
            {/* Auth Section */}
            <div className="flex items-center gap-3">
              {user ? (
                <div className="flex items-center gap-3 bg-white/10 rounded-full px-4 py-2">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <User className="w-8 h-8 text-cyan-400" />
                  )}
                  <span className="text-sm font-medium">{user.name}</span>
                  <button 
                    onClick={handleLogout}
                    className="text-slate-300 hover:text-white ml-2"
                    title="Sign out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleGoogleSignIn}
                  className="flex items-center gap-2 bg-white text-slate-800 font-medium px-4 py-2 rounded-full hover:bg-slate-100 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Message */}
      {message && (
        <div className="max-w-6xl mx-auto px-6 mt-4">
          <div className={`p-4 rounded-lg ${message.includes('success') || message.includes('Successfully') ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
            {message}
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Write Review Section */}
        {user && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            {myReview && !showForm ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-slate-800">Your Review</h2>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      myReview.status === 'approved' ? 'bg-green-100 text-green-700' :
                      myReview.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {myReview.status === 'approved' ? <Check className="w-3 h-3 inline mr-1" /> : null}
                      {myReview.status}
                    </span>
                    <button 
                      onClick={() => setShowForm(true)}
                      className="p-2 text-slate-600 hover:text-cyan-600 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={handleDeleteReview}
                      className="p-2 text-slate-600 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <StarRating rating={myReview.rating} />
                <h3 className="font-semibold text-lg mt-3">{myReview.title}</h3>
                <p className="text-slate-600 mt-2">{myReview.content}</p>
                {myReview.company_name && (
                  <p className="text-sm text-slate-500 mt-2">— {myReview.company_name}</p>
                )}
              </div>
            ) : showForm || !myReview ? (
              <form onSubmit={handleSubmitReview}>
                <h2 className="text-xl font-bold text-slate-800 mb-4">
                  {myReview ? 'Edit Your Review' : 'Write a Review'}
                </h2>
                
                <div className="space-y-4">
                  {/* Rating */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Rating</label>
                    <StarRating 
                      rating={formData.rating} 
                      onRate={(rating) => setFormData({ ...formData, rating })}
                      interactive
                    />
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                    <input
                      type="text"
                      required
                      minLength={3}
                      maxLength={100}
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Summarize your experience"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Review</label>
                    <textarea
                      required
                      minLength={10}
                      maxLength={1000}
                      rows={4}
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Tell us about your experience working with NEURA AI SOFTWARES..."
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>

                  {/* Company & Industry */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        Company Name (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        placeholder="Your company"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <Briefcase className="w-4 h-4 inline mr-1" />
                        Industry (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.industry}
                        onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                        placeholder="e.g., Healthcare, Retail"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50"
                    >
                      {submitting ? 'Submitting...' : myReview ? 'Update Review' : 'Submit Review'}
                    </button>
                    {showForm && myReview && (
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="px-6 py-3 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </form>
            ) : null}
          </div>
        )}

        {/* Call to action for non-logged in users */}
        {!user && (
          <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl p-8 mb-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-3">Share Your Experience</h2>
            <p className="text-cyan-100 mb-6">Sign in with Google to leave a review about working with us</p>
            <button
              onClick={handleGoogleSignIn}
              className="inline-flex items-center gap-2 bg-white text-slate-800 font-semibold px-6 py-3 rounded-full hover:bg-slate-100 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in to Write a Review
            </button>
          </div>
        )}

        {/* Reviews List */}
        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-6">
            What Our Clients Say
            <span className="text-slate-400 text-lg font-normal ml-2">({reviews.length} reviews)</span>
          </h2>

          {reviews.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl">
              <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No reviews yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((review) => (
                <div 
                  key={review.review_id}
                  className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {review.user_picture ? (
                        <img 
                          src={review.user_picture} 
                          alt={review.user_name}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-cyan-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-slate-800">{review.user_name}</p>
                        {review.company_name && (
                          <p className="text-sm text-slate-500">{review.company_name}</p>
                        )}
                      </div>
                    </div>
                    <StarRating rating={review.rating} />
                  </div>
                  
                  <h3 className="font-semibold text-lg text-slate-800 mb-2">{review.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{review.content}</p>
                  
                  {review.industry && (
                    <span className="inline-block mt-3 px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                      {review.industry}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p>© {new Date().getFullYear()} NEURA AI SOFTWARES. All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ReviewsPage;
